import { type ChangeEvent, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGlobalState } from "senf-shared";
import { cn } from "../../../../util/classNameConstructor";
import ListItem from "../../../core/list/ListItem";
import { AlphaNum } from "../questions/ImageChoice";
import type { SelectProps } from "../questions/MultipleChoice";
import {
  type InputColumn,
  type InputRow,
  type Page,
  type PageType,
  pageTypeConfig,
} from "../survey.config";
import { isChecked, type useEditQuestion } from "../utils";
import EditableContent from "./EditableContent";
import { HandleItem } from "./HandleItem";
import OptimizedInvisibleInput from "./OptimizedInvisibleInput";

export const Row = ({
  column,
  checked,
  setChecked,
  index,
  mode,
  inputType,
  secondaryContent,
  draggable,
  placeholder,
  editable,
  isDisabled,
  disabledNotice,
  defaultValue,
  tabIndex,
  className,
  ...props
}: SelectProps & {
  setChecked: React.Dispatch<React.SetStateAction<string | string[]>>;
  columns: InputColumn<PageType>[];
  column: InputRow<PageType>;
  checked: string | string[];
  secondaryContent?: React.ReactNode;
  index: number;
  inputType: "radio" | "checkbox";
  draggable?: boolean;
  placeholder?: (index: number) => string;
  editable?: boolean;
  isDisabled?: boolean;
  disabledNotice?: string;
  defaultValue?: string;
  className?: string;
  tabIndex?: number;
} & ReturnType<typeof useEditQuestion>) => {
  const { t } = useTranslation();
  const [translation] = useGlobalState().translationsState;

  const [active, setActive] = useState(false);

  // Memoize expensive computations to prevent unnecessary rerenders
  const displayValue = useMemo(
    () => translation?.[column.label || ""] || column.label,
    [translation, column.label],
  );

  const isOptionDisabled = useMemo(() => {
    if (isDisabled) return true;
    return !!(
      props.settings?.multipleChoice?.enabled &&
      props.settings?.multipleChoice?.max &&
      Array.isArray(checked) &&
      checked.length >= props.settings?.multipleChoice?.max &&
      !isChecked(checked, column.uid, inputType)
    );
  }, [
    isDisabled,
    props.settings?.multipleChoice,
    checked,
    column.uid,
    inputType,
  ]);

  const hasConditions = useMemo(
    () => props.conditions?.some((e) => e.type !== "none") || false,
    [props.conditions],
  );

  const isDeletable = useMemo(() => {
    const defaults = pageTypeConfig[props.page.type]?.defaults;
    const minColumns =
      typeof defaults === "function"
        ? defaults()?.minColumns
        : defaults?.minColumns;

    return props.columns.length > (minColumns || 0) || column.uid === "add";
  }, [props.columns.length, props.page.type]);

  // Memoize event handlers to prevent child component rerenders
  const handleChange = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = target?.value || "";
      if (props.onChange) {
        props.onChange({ value });
        return;
      }
      // Handle "other" option specially - update otherTitle instead of options array
      if (column.uid === "other") {
        props.updateOtherOption({
          label: target?.value ?? null,
        });
      } else {
        props.updateItemText(target?.value, column.uid, "columns", "label");
      }
    },
    [props.onChange, props.updateText, props.updateQuestion, column.uid],
  );

  const handleDelete = useCallback(() => {
    props.removeItem(column.uid, "columns", { focus: true });
  }, [props.removeItem, column.uid]);

  const handleFocus = useCallback(() => setActive(true), []);
  const handleBlur = useCallback(() => setActive(false), []);

  return (
    <div className={cn("relative group/cell flex w-auto items-center -mx-2")}>
      <HandleItem
        active={active}
        deletable={isDeletable}
        draggable={draggable}
        handleDelete={handleDelete}
        mode={mode}
        className="absolute -start-4"
        axis="row"
      />
      <ListItem
        index={index}
        tag="div"
        noTooltip={mode === "edit"}
        className="w-auto grow"
        primaryContentProps={{
          className: "line-clamp-none",
        }}
        primaryContent={
          <div className={cn("flex items-center justify-between", className)}>
            <OptimizedInvisibleInput
              id={column.uid}
              size="md"
              className="body-md"
              prepend={
                hasConditions &&
                mode === "edit" && (
                  <AlphaNum
                    index={index}
                    checked={isChecked(checked, column.uid, inputType)}
                    className={"-ms-1.5 -my-2 me-1"}
                  />
                )
              }
              value={displayValue}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={
                placeholder?.(index) || `${t("option")} ${index + 1}`
              }
              onClick={(e) => e.stopPropagation()}
              classNames={{
                inputContainer: "ms-1 me-1",
              }}
              onFocus={handleFocus}
              mode={editable === false ? "display" : mode}
              defaultValue={defaultValue}
            />
            {secondaryContent}
          </div>
        }
        classNames={{
          wrapper: "w-full",
          base: cn(
            "my-0.5 h-auto min-h-[38px] gap-0 py-0",
            mode === "edit" && "cursor-grab",
          ),
        }}
        lines={1}
        selectable
        selectableType={inputType}
        hoverable={mode === "display"}
        disabled={isOptionDisabled}
        disabledNotice={
          disabledNotice ||
          (props.settings?.multipleChoice?.max
            ? t("survey:disabledMessages.radio.maxChoices", {
                count: props.settings?.multipleChoice?.max,
              })
            : undefined)
        }
        tabIndex={mode === "edit" ? -1 : (tabIndex ?? index)}
      />
    </div>
  );
};
